import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Row, Col, Card, Table, FormControl, InputGroup } from 'react-bootstrap';
import { useAuth0 } from '@auth0/auth0-react';
import '../../styles/MenuAndBillModal.css';
import useItemManagement from '../../hooks/useItemManagement';
import { useUserPermissions, useVenueId } from '../../hooks/useUserData';
import { calculateItemTotal, calculateTotal, formatModifierSummary } from '../../utils/itemUtils';
import { useVenueFeedback } from '../../context/VenueFeedbackContext';
import { updateVenueItemAvailability } from '../../utils/staffApiService';
import ItemsChangeModal from './ItemsChangeModal';
import ModifierPickerModal from './ModifierPickerModal';

const AVAILABILITY_PERMISSIONS = ['edit_orders', 'take_payment', 'manage_shift', 'manage_menu'];
const STALE_MENU_ERROR_CODES = new Set(['ITEM_NOT_ORDERABLE', 'ITEM_UNAVAILABLE']);

const getBlockedItemCount = (error) => {
  const itemIds = error?.payload?.itemIds;
  return Array.isArray(itemIds) ? itemIds.length : 0;
};

const MenuAndBillModal = ({ show, handleClose, menu, tableSummary, saveOrder, onSettleTable, contextType }) => {
  const venueId = useVenueId();
  const userPermissions = useUserPermissions();
  const { getAccessTokenSilently } = useAuth0();
  const isViewBill = contextType === 'ViewBill';
  const isCashierAddItems = contextType === 'CashierAddItems';
  const isNewOrderFlow = contextType === 'NewOrder';
  const canEditItems = !isViewBill;
  const initialItems = useMemo(() => (isNewOrderFlow || isCashierAddItems ? [] : tableSummary), [isCashierAddItems, isNewOrderFlow, tableSummary]);
  const menuCategories = useMemo(() => menu?.categories || [], [menu?.categories]);
  const refreshMenu = menu?.refreshMenu;
  const canToggleAvailability = userPermissions.some((permission) => AVAILABILITY_PERMISSIONS.includes(permission));

  const {
    selectedItems,
    handleItemClick,
    handleQuantityChange,
    handleItemNoteChange,
    handleDeleteItem,
    hasChanges,
    changes,
    resetToInitialState,
  } = useItemManagement(initialItems);
  const { pushFeedback } = useVenueFeedback();

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showConfirmNewOrder, setShowConfirmNewOrder] = useState(false);
  const [availabilityMode, setAvailabilityMode] = useState(false);
  const [isSettling, setIsSettling] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdatingAvailability, setIsUpdatingAvailability] = useState(false);
  const [orderNote, setOrderNote] = useState('');
  const [settleError, setSettleError] = useState('');
  const [modifierItem, setModifierItem] = useState(null);

  useEffect(() => {
    if (menuCategories.length === 0) {
      setSelectedCategory(null);
      return;
    }

    if (menuCategories.some((category) => String(category.id) === String(selectedCategory))) {
      return;
    }

    setSelectedCategory(menuCategories[0].id);
  }, [menuCategories, selectedCategory]);

  useEffect(() => {
    if (!show) {
      return;
    }

    resetToInitialState();
    setOrderNote('');
    setSettleError('');
    setAvailabilityMode(false);
  }, [show, contextType, resetToInitialState]);

  const activeCategory = menuCategories.find(
    (category) => String(category.id) === String(selectedCategory)
  ) || null;
  const availableItems = (activeCategory?.items || []).filter((item) => item?.isAvailable !== false);
  const allCategoryItems = activeCategory?.items || [];
  const unavailableCount = menuCategories.reduce(
    (count, category) => count + (category.items || []).filter((item) => item?.isAvailable === false).length,
    0
  );
  const itemLineCount = selectedItems.length;
  const totalAmount = calculateTotal(selectedItems);

  const handleSave = () => {
    setShowConfirmNewOrder(true);
  };

  const handleSettleTable = async () => {
    if (!onSettleTable || isSettling) return;

    setIsSettling(true);
    setSettleError('');
    try {
      const result = await onSettleTable();
      const settledOrders = result?.settledOrders ?? 0;
      const closedRequests = result?.closedRequests ?? 0;
      pushFeedback({
        type: 'success',
        title: 'Masa hesabı kapatıldı',
        autoHideMs: 6000,
        message:
          `Ödenen sipariş: ${settledOrders}\n` +
          `Kapatılan hesap isteği: ${closedRequests}`,
      });
      handleClose();
    } catch (error) {
      const message = error?.message || 'Hesap kapatma işlemi başarısız oldu.';
      if (error?.code === 'ACTIVE_ORDERS_PRESENT' || message.includes('ACTIVE_ORDERS_PRESENT')) {
        setSettleError('Masa üzerinde aktif siparişler var. Önce bu siparişleri tamamlayın.');
        pushFeedback({
          type: 'warning',
          title: 'Hesap kapatılamadı',
          message: 'Masa üzerinde aktif siparişler var. Önce bu siparişleri tamamlayın.',
        });
      } else {
        setSettleError(`Hesap kapatma işlemi başarısız oldu: ${message}`);
        pushFeedback({
          type: 'error',
          title: 'Hesap kapatılamadı',
          message: `Hesap kapatma işlemi başarısız oldu: ${message}`,
        });
      }
    } finally {
      setIsSettling(false);
    }
  };

  const handleRecoverableSaveError = async (error) => {
    if (!STALE_MENU_ERROR_CODES.has(error?.code)) {
      return;
    }

    if (typeof refreshMenu === 'function') {
      await refreshMenu();
    }

    const blockedItemCount = getBlockedItemCount(error);
    pushFeedback({
      type: 'warning',
      title: 'Menü güncellendi',
      message: blockedItemCount > 0
        ? `${blockedItemCount} ürün artık siparişe açık değil. Menü yenilendi, lütfen tekrar kontrol edin.`
        : 'Bazı ürünler artık siparişe açık değil. Menü yenilendi, lütfen tekrar kontrol edin.',
    });
  };

  const handleConfirmSaveOrder = async (order) => {
    setIsSaving(true);
    try {
      await saveOrder(order);
      setShowConfirmNewOrder(false);
      handleClose();
    } catch (error) {
      await handleRecoverableSaveError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleMenuItemClick = (item) => {
    if (item?.isAvailable === false) {
      return;
    }

    if ((item.modifierGroups || []).length > 0) {
      setModifierItem(item);
      return;
    }

    handleItemClick(item);
  };

  const handleAvailabilityToggle = async (item, isAvailable) => {
    if (isUpdatingAvailability) {
      return;
    }

    setIsUpdatingAvailability(true);
    try {
      await updateVenueItemAvailability(venueId, item.id, isAvailable, getAccessTokenSilently);
      if (typeof refreshMenu === 'function') {
        await refreshMenu();
      }
      pushFeedback({
        type: 'success',
        title: isAvailable ? 'Ürün satışa açıldı' : 'Ürün geçici olarak kapatıldı',
        message: isAvailable
          ? `${item.name} yeniden siparişe açıldı.`
          : `${item.name} artık siparişe kapalı.`,
      });
    } catch (error) {
      pushFeedback({
        type: 'error',
        title: 'Ürün güncellenemedi',
        message: error?.message || 'Ürünün satış durumu güncellenemedi.',
      });
    } finally {
      setIsUpdatingAvailability(false);
    }
  };

  const getTitle = () => {
    switch (contextType) {
      case 'EditOrder':
        return 'Siparişi Düzenle';
      case 'CashierAddItems':
        return 'Adisyona Ürün Ekle';
      case 'NewOrder':
        return 'Yeni Sipariş Oluştur';
      case 'ViewBill':
      default:
        return 'Hesap Özeti';
    }
  };

  const renderMenuCard = (item) => (
    <Card
      key={item.id}
      className="item-card"
      onClick={() => handleMenuItemClick(item)}
    >
      {item.image ? <Card.Img variant="top" src={item.image} /> : <div className="item-card__placeholder">Görsel yok</div>}
      <Card.Body>
        <Card.Title>{item.name}</Card.Title>
        <Card.Text>{item.price} TRY</Card.Text>
      </Card.Body>
    </Card>
  );

  return (
    <>
      <Modal show={show} onHide={handleClose} size="xl" dialogClassName="venue-menu-modal">
        <Modal.Header closeButton>
          <Modal.Title>{getTitle()}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isViewBill ? (
            <div className="bill-summary bill-summary--full">
              <div className="bill-summary__header">
                <h5 className="mb-1">Hesap Özeti</h5>
                <p className="mb-0 text-muted">
                  Bu görünüm yalnızca mevcut masa hesabını gösterir. Yeni ürün eklemek için
                  {' '}
                  <strong>+ Yeni Sipariş</strong>
                  {' '}
                  aksiyonunu kullanın.
                </p>
              </div>
              {settleError ? (
                <div className="venue-flow-alert venue-flow-alert--danger">
                  <strong>Hesap kapatılamadı</strong>
                  {'\n'}
                  {settleError}
                </div>
              ) : null}
              <div className="venue-modal-summary">
                <div className="venue-modal-summary__item">
                  <span className="venue-modal-summary__label">Ürün satırı</span>
                  <span className="venue-modal-summary__value">{itemLineCount}</span>
                </div>
                <div className="venue-modal-summary__item">
                  <span className="venue-modal-summary__label">Toplam tutar</span>
                  <span className="venue-modal-summary__value">{totalAmount} TRY</span>
                </div>
              </div>
              <div className="bill-summary__table-wrap">
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Adet</th>
                      <th>Ürün Adı</th>
                      <th>Birim (TRY)</th>
                      <th>Toplam (TRY)</th>
                      {contextType !== 'ViewBill' ? <th /> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedItems.map((item, index) => (
                      <tr key={item.orderItemId || item.id || index}>
                        <td>
                          <InputGroup>
                            {contextType !== 'ViewBill' ? (
                              <Button variant="outline-secondary" onClick={() => handleQuantityChange(item.lineKey, 1)}>+</Button>
                            ) : null}
                            <FormControl
                              type="text"
                              value={item.quantity}
                              readOnly
                              style={{
                                textAlign: 'center',
                                width: '42px',
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                padding: '0.25rem',
                                backgroundColor: 'transparent',
                                border: 'none',
                              }}
                            />
                            {contextType !== 'ViewBill' ? (
                              <Button variant="outline-secondary" onClick={() => handleQuantityChange(item.lineKey, -1)}>-</Button>
                            ) : null}
                          </InputGroup>
                        </td>
                        <td>
                          <div>{item.name}</div>
                          {item.modifiers?.length ? (
                            <div className="small text-muted mt-1">{formatModifierSummary(item.modifiers)}</div>
                          ) : null}
                          {contextType === 'NewOrder' ? (
                            <div className="mt-2">
                              <FormControl
                                as="textarea"
                                rows={2}
                                maxLength={200}
                                placeholder="Ürün notu"
                                value={item.notes || ''}
                                onChange={(event) => handleItemNoteChange(item.lineKey, event.target.value)}
                              />
                              <div className="text-muted small mt-1 text-end">{(item.notes || '').length}/200</div>
                            </div>
                          ) : item.notes ? (
                            <div className="small text-muted mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                              <strong>Not:</strong> {item.notes}
                            </div>
                          ) : null}
                        </td>
                        <td>{item.price.toFixed(2)}</td>
                        <td>{calculateItemTotal(item)}</td>
                        {canEditItems ? (
                          <td>
                            <Button variant="outline-danger" onClick={() => handleDeleteItem(item.lineKey)}>Sil</Button>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <div className="total">
                <strong>Toplam: {totalAmount} TRY</strong>
              </div>
            </div>
          ) : (
            <Row className="g-4">
              <Col lg={7} className="menu-section">
                <div className="menu-section__header">
                  <div className="menu-section__header-row">
                    <div>
                      <h5 className="mb-1">{availabilityMode ? 'Ürün Durumu' : 'Menü'}</h5>
                      <p className="mb-0 text-muted">
                        {availabilityMode
                          ? 'Ürünleri geçici olarak satışa kapatın veya açın.'
                          : 'Kategoriler arasında geçiş yapın ve ürün ekleyin.'}
                      </p>
                    </div>
                    {canToggleAvailability ? (
                      <button
                        type="button"
                        className={`availability-mode-toggle ${availabilityMode ? 'active' : ''}`}
                        onClick={() => setAvailabilityMode((current) => !current)}
                      >
                        {availabilityMode ? 'Menüye Dön' : 'Ürün Durumu'}
                        {!availabilityMode && unavailableCount > 0 ? (
                          <span className="availability-mode-toggle__badge">{unavailableCount}</span>
                        ) : null}
                      </button>
                    ) : null}
                  </div>
                </div>
                <div className="category-nav">
                  {menuCategories.map((category) => (
                    <button
                      key={category.id}
                      type="button"
                      className={`category-nav__button ${String(selectedCategory) === String(category.id) ? 'active' : ''}`}
                      onClick={() => setSelectedCategory(category.id)}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
                {availabilityMode ? (
                  <div className="availability-list">
                    {allCategoryItems.length === 0 ? (
                      <div className="venue-empty-state">
                        <p className="venue-empty-state__title">Kategori boş</p>
                        <p className="venue-empty-state__text">Bu kategoride aktif ürün yok.</p>
                      </div>
                    ) : (
                      allCategoryItems.map((item) => {
                        const isUnavailable = item?.isAvailable === false;
                        return (
                          <div key={item.id} className={`availability-list__row ${isUnavailable ? 'availability-list__row--closed' : ''}`}>
                            <div className="availability-list__info">
                              <span className="availability-list__name">{item.name}</span>
                              <span className="availability-list__price">{item.price} TRY</span>
                            </div>
                            <Button
                              variant={isUnavailable ? 'outline-primary' : 'outline-secondary'}
                              size="sm"
                              onClick={() => handleAvailabilityToggle(item, isUnavailable)}
                              disabled={isUpdatingAvailability}
                            >
                              {isUnavailable ? 'Satışa Aç' : 'Satışa Kapat'}
                            </Button>
                          </div>
                        );
                      })
                    )}
                  </div>
                ) : (
                  <div className="items">
                    {availableItems.length === 0 ? (
                      <div className="venue-empty-state">
                        <p className="venue-empty-state__title">Kategori boş</p>
                        <p className="venue-empty-state__text">Bu kategoride siparişe açık aktif ürün yok.</p>
                      </div>
                    ) : (
                      availableItems.map((item) => renderMenuCard(item))
                    )}
                  </div>
                )}
              </Col>
              <Col lg={5} className="bill-summary">
                <div className="bill-summary__header">
                  <h5 className="mb-1">Hesap Özeti</h5>
                  <p className="mb-0 text-muted">Ürünler ve toplam tutar burada özetlenir.</p>
                </div>
                <div className="bill-summary__table-wrap">
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Adet</th>
                        <th>Ürün Adı</th>
                        <th>Birim (TRY)</th>
                        <th>Toplam (TRY)</th>
                        {contextType !== 'ViewBill' ? <th /> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {selectedItems.map((item, index) => (
                        <tr key={item.orderItemId || item.id || index}>
                          <td>
                            <InputGroup>
                              {contextType !== 'ViewBill' ? (
                                <Button variant="outline-secondary" onClick={() => handleQuantityChange(item.lineKey, 1)}>+</Button>
                              ) : null}
                              <FormControl
                                type="text"
                                value={item.quantity}
                                readOnly
                                style={{
                                  textAlign: 'center',
                                  width: '42px',
                                  fontSize: '0.9rem',
                                  fontWeight: '600',
                                  padding: '0.25rem',
                                  backgroundColor: 'transparent',
                                  border: 'none',
                                }}
                              />
                              {contextType !== 'ViewBill' ? (
                                <Button variant="outline-secondary" onClick={() => handleQuantityChange(item.lineKey, -1)}>-</Button>
                              ) : null}
                            </InputGroup>
                          </td>
                          <td>
                            <div>{item.name}</div>
                            {item.modifiers?.length ? (
                              <div className="small text-muted mt-1">{formatModifierSummary(item.modifiers)}</div>
                            ) : null}
                            {isNewOrderFlow || isCashierAddItems ? (
                              <div className="mt-2">
                                <FormControl
                                  as="textarea"
                                  rows={2}
                                  maxLength={200}
                                  placeholder="Ürün notu"
                                  value={item.notes || ''}
                                  onChange={(event) => handleItemNoteChange(item.lineKey, event.target.value)}
                                />
                                <div className="text-muted small mt-1 text-end">{(item.notes || '').length}/200</div>
                              </div>
                            ) : item.notes ? (
                              <div className="small text-muted mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                                <strong>Not:</strong> {item.notes}
                              </div>
                            ) : null}
                          </td>
                          <td>{item.price.toFixed(2)}</td>
                          <td>{calculateItemTotal(item)}</td>
                          {canEditItems ? (
                            <td>
                              <Button variant="outline-danger" onClick={() => handleDeleteItem(item.lineKey)}>Sil</Button>
                            </td>
                          ) : null}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <div className="total">
                  <strong>Toplam: {totalAmount} TRY</strong>
                </div>
                {isNewOrderFlow || isCashierAddItems ? (
                  <div className="mt-3">
                    <label className="form-label">{isCashierAddItems ? 'Adisyon notu' : 'Sipariş notu'}</label>
                    <FormControl
                      as="textarea"
                      rows={3}
                      maxLength={500}
                      placeholder="Örn. alerji veya servis notu"
                      value={orderNote}
                      onChange={(event) => setOrderNote(event.target.value)}
                    />
                    <div className="text-muted small mt-1 text-end">{orderNote.length}/500</div>
                  </div>
                ) : null}
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="venue-modal-actions">
            <Button variant="outline-secondary" onClick={handleClose}>
              Kapat
            </Button>
            <div className="venue-modal-actions__spacer" />
            {contextType === 'ViewBill' ? (
              <Button variant="success" onClick={handleSettleTable} disabled={isSettling}>
                {isSettling ? 'Kapatılıyor...' : 'Hesabı Kapat'}
              </Button>
            ) : (
              <Button variant="primary" onClick={handleSave} disabled={!hasChanges || isSaving}>
                {isSaving ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>
      <ItemsChangeModal
        show={showConfirmNewOrder}
        handleClose={() => setShowConfirmNewOrder(false)}
        orderItems={changes}
        orderNote={orderNote}
        handleConfirmChanges={handleConfirmSaveOrder}
        mode={isNewOrderFlow ? 'new-order' : isCashierAddItems ? 'cashier-add' : 'edit-order'}
      />
      <ModifierPickerModal
        show={Boolean(modifierItem)}
        item={modifierItem}
        onClose={() => setModifierItem(null)}
        onConfirm={(configuredItem) => {
          handleItemClick(configuredItem);
          setModifierItem(null);
        }}
        title="Ürünü Yapılandır"
      />
    </>
  );
};

export default MenuAndBillModal;
